import * as React from 'react';
import { observer } from 'mobx-react';
import { endpoints } from '../../../config';
import security from '../../common/services/SecurityService';
import { VisualBlocksVisualStore } from '../stores';
import InteractiveBlock from './InteractiveBlock';

type Props = {
    store: VisualBlocksVisualStore;
    pageHeaderHeight?: number;
    renderBlocks?: boolean;
};

type State = {
    imageUrl: string;
    renderedPackageId: string | null;
};

class VisualBlocksImagePreview extends React.Component<Props, State> {
    imageRef: HTMLImageElement | null = null;
    pageHeaderHeight = this.props.pageHeaderHeight || 140;
    loadedPackageId: string | null = null;
    mounted = false;

    state: State = {
        imageUrl: '',
        renderedPackageId: null
    };

    componentDidMount() {
        this.mounted = true;
        window.addEventListener('resize', this.syncImageWidth);
        this.loadImageUrl();
        this.syncPagesInfo();
    }

    componentDidUpdate() {
        this.loadImageUrl();
        this.syncPagesInfo();
        this.syncImageWidth();
    }

    componentWillUnmount() {
        this.mounted = false;
        window.removeEventListener('resize', this.syncImageWidth);
    }

    loadImageUrl = () => {
        const selectedPackage = this.props.store.selectedPackage;
        if (!selectedPackage) {
            this.loadedPackageId = null;
            if (this.state.imageUrl || this.state.renderedPackageId) {
                this.setState({ imageUrl: '', renderedPackageId: null });
            }
            return;
        }

        if (selectedPackage.id === this.loadedPackageId) {
            return;
        }

        this.loadedPackageId = selectedPackage.id;
        this.imageRef = null;
        this.setState({ imageUrl: '', renderedPackageId: null });

        security.invoke(token => {
            if (!this.mounted || this.loadedPackageId !== selectedPackage.id) {
                return Promise.resolve();
            }

            const path = encodeURIComponent(selectedPackage.id);
            const accessToken = encodeURIComponent(token);
            this.setState({ imageUrl: `${endpoints.manageUrl}document/${path}?access_token=${accessToken}` });
            return Promise.resolve();
        });
    };

    syncImageWidth = () => {
        const selectedPackage = this.props.store.selectedPackage;
        if (!this.imageRef || !selectedPackage || this.state.renderedPackageId !== selectedPackage.id) {
            return;
        }

        this.props.store.setPageWidth(this.imageRef.clientWidth);
    };

    syncPagesInfo = () => {
        if (this.props.store.isLoading) {
            return;
        }

        let maxPage = 0;
        this.props.store.lines.forEach(line => {
            const page = line.coordinates?.page;
            if (typeof page === 'number' && page > maxPage) {
                maxPage = page;
            }
        });
        const totalPages = maxPage + 1;

        if (this.props.store.totalPages !== totalPages) {
            this.props.store.setPagesInfo(totalPages);
        }
    };

    renderBlocks() {
        const store = this.props.store;

        if (
            !this.props.renderBlocks ||
            !store.pageWidth ||
            store.isLoading ||
            this.state.renderedPackageId !== store.selectedPackage?.id
        ) {
            return null;
        }

        return store
            .getBlocksForPage(store.currentPage)
            .map((block, index) => (
                <InteractiveBlock key={`${store.currentPage}_image_${index}_block`} block={block} />
            ));
    }

    render() {
        if (!this.props.store.selectedPackage || !this.state.imageUrl) {
            return <div />;
        }

        return (
            <div
                style={{
                    padding: 10,
                    height: `calc(100vh - ${this.pageHeaderHeight}px)`,
                    overflow: 'auto',
                    textAlign: 'center'
                }}
            >
                <div style={{ display: 'inline-block', position: 'relative', maxWidth: '100%' }}>
                    <img
                        alt={this.props.store.selectedPackage.name}
                        ref={ref => (this.imageRef = ref)}
                        src={this.state.imageUrl}
                        onLoad={() => {
                            this.setState({ renderedPackageId: this.props.store.selectedPackage?.id || null });
                        }}
                        style={{ display: 'block', maxWidth: '100%', height: 'auto' }}
                    />
                    {this.renderBlocks()}
                </div>
            </div>
        );
    }
}

export default observer(VisualBlocksImagePreview);
